import { ProductCode, AddOnType, BillingPlanCategory } from '@dev/ee-api/ee-api-models';
import { isEmpty } from 'lodash-es';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProviders, LoadPaymentProviders } from '@/store/payments/actions';
import dayjs from '@/services/tools/dayjs';
import { useActivePlan } from '../useActivePlan';
import { usePaymentError } from '../usePaymentError';

// downgrade is only available for 2 days before the renewal of subscription
const checkIfAccountCanDowngrade = (billingDate: string) => {
    const daysBeforeRenewalOfPlan = 2;

    const billingDateAsUTC = dayjs(billingDate).utc(true);

    const daysTillSubscriptionRenewal = billingDateAsUTC.diff(dayjs().utc(), 'd');
    const canDowngrade = daysTillSubscriptionRenewal <= daysBeforeRenewalOfPlan;

    return {
        canDowngrade,
        earliestDateForDowngradeUTC: billingDateAsUTC.subtract(daysBeforeRenewalOfPlan, 'd').toString(),
    };
};

type CurrentPlanStatusT = {
    isActive: boolean;
    isExpired: boolean;
    isTrial: boolean;
    isFree: boolean;
    isTrialOrFree: boolean;
    isPilotPlan: boolean;
    isStandardPlan: boolean;
    isProPlan: boolean;
    isOffState: boolean;
    isCanceled: boolean;
    shouldRenewPlan: boolean;
    canDowngrade: boolean;
    earliestDateForDowngradeUTC: string;
    productCode: ProductCode;
};

export const useCurrentPlanStatus = (): CurrentPlanStatusT => {
    const dispatch = useDispatch();

    const { ActivePlanDetails, Code } = useActivePlan();
    const { anyPaymentError } = usePaymentError();
    const providers = useSelector(getProviders);
    const { NextBillingDate, IsTrial, IsActive, IsFree, IsExpired } = ActivePlanDetails || {};

    useEffect(() => {
        if (!providers) {
            dispatch(LoadPaymentProviders());
        }
    }, []);

    const isTrialOrFree = IsFree || IsTrial;

    const isExpired = IsExpired;
    const isActive = IsActive;
    const isCanceled = ActivePlanDetails?.IsSubscriptionBased && !IsTrial && !isActive && !isExpired;
    const availableAddons = ActivePlanDetails?.AddOns?.filter(
        (addon) => Code === ProductCode.Marketing && addon?.Type !== AddOnType.ExtendedLogs
    );
    const hasAnyAddons = availableAddons?.some((addon) => addon.IsPurchased);

    const shouldRenewPlan = isEmpty(providers) && isExpired && hasAnyAddons;

    const { canDowngrade, earliestDateForDowngradeUTC } = checkIfAccountCanDowngrade(NextBillingDate);

    const isPilotPlan = ActivePlanDetails?.Category === BillingPlanCategory.Pilot;
    const isStandardPlan = ActivePlanDetails?.Category === BillingPlanCategory.Standard;
    const isProPlan = ActivePlanDetails?.Category === BillingPlanCategory.Pro;
    return {
        isActive,
        isExpired,
        isFree: IsFree,
        isTrial: IsTrial,
        isTrialOrFree,
        isPilotPlan,
        isStandardPlan,
        isProPlan,
        isOffState: anyPaymentError,
        isCanceled,
        shouldRenewPlan,
        canDowngrade,
        earliestDateForDowngradeUTC,
        productCode: Code,
    };
};
